'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DEFAULT_LOTAME_AUDIENCE_LIMIT = undefined;

var _get = require('lodash/get');

var _get2 = _interopRequireDefault(_get);

var _noop = require('lodash/noop');

var _noop2 = _interopRequireDefault(_noop);

var _isUndefined = require('lodash/isUndefined');

var _isUndefined2 = _interopRequireDefault(_isUndefined);

var _logger = require('../logging/logger');

var _logger2 = _interopRequireDefault(_logger);

var _applicationState = require('../utils/applicationState');

var _htmlUtils = require('../utils/htmlUtils');

var _htmlUtils2 = _interopRequireDefault(_htmlUtils);

var _parseUtils = require('../utils/parseUtils');

var _parseUtils2 = _interopRequireDefault(_parseUtils);

var _localStorageController = require('../utils/localStorageController');

var _localStorageController2 = _interopRequireDefault(_localStorageController);

var _constants = require('../utils/constants');

var _constants2 = require('../logging/constants');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var DEFAULT_LOTAME_CLIENT_ID = 10306;
var DEFAULT_LOTAME_AUDIENCE_LIMIT = exports.DEFAULT_LOTAME_AUDIENCE_LIMIT = 40;

var lotameNetworkController = {
  get lotame() {
    // eslint-disable-next-line no-undef
    return window['lotame_' + this.lotameClientId];
  },

  set lotame(lotame) {
    // eslint-disable-next-line no-undef
    window['lotame_' + this.lotameClientId] = lotame;
  },

  get lotameClientId() {
    if (!this._lotameClientId) {
      var lotameClientId = (0, _get2.default)((0, _applicationState.getState)(_applicationState.State.options), 'lotame.lotameClientId', DEFAULT_LOTAME_CLIENT_ID);
      this._lotameClientId = Number(lotameClientId);
    }

    return this._lotameClientId;
  },

  /**
   * Initializes the Lotame script
   */
  init: function init() {
    if (!this.isLotameEnabled()) {
      return;
    }

    this.handleLotameReady = this.handleLotameReady.bind(this);
    this.handleAudiencesReady = this.handleAudiencesReady.bind(this);
    this.setConsent = this.setConsent.bind(this);
    this.initLotame = this.initLotame.bind(this);

    this.preConfigureLotame();
    this.loadCachedAudiences();
    this.initLotame();
  },


  /**
   * Sets up the required lotame object on the window
   */
  preConfigureLotame: function preConfigureLotame() {
    // https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#lightning-tag-setup
    this.lotame = {
      // https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#config-object
      config: {
        clientId: this.lotameClientId,
        audienceLocalStorage: _constants.LocalStorage.AUDIENCES,
        onProfileReady: this.handleAudiencesReady,
        onTagReady: this.handleLotameReady
      },

      // probably safe to initialized without this, but better to be safe than sorry
      data: {},

      // `cmd` isn't explicitly documented, but this is a function queue that lotame uses to
      // asynchronously handle calls to "Lightning Tag Methods". LTM doc & examples here:
      // https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#lightning-tag-methods
      cmd: []
    };
  },
  loadCachedAudiences: function loadCachedAudiences() {
    // Preemptively using cached audiences here (if any) to load audiences early or in case the
    // lightning tag setup fails for any reason.
    // Lotame caches audiences on its own. See config in preConfigureLotame() and refer to docs.
    var cachedAudiences = this.getCachedAudiences();
    if (cachedAudiences) {
      this.updateAudiences(cachedAudiences);
    }
  },


  /**
   * Initializes the Lotame Lightning Tag script and loads cached "audiences" into state.
   * However, we need to delay these tasks in order for OneTrust in Gemini-Web to determine
   * GDPR applicability. If GDPR is not applicable or if GDPR is applicable and user consent
   * has been captured, we can proceed, otherwise we register this function as a listener for
   * updates to the GDPR state.
   */
  initLotame: function initLotame() {
    var _getState = (0, _applicationState.getState)(_applicationState.State.gdprData, {}),
        gdprApplies = _getState.gdprApplies,
        hasConsentBeenCaptured = _getState.hasConsentBeenCaptured;

    if ((0, _isUndefined2.default)(gdprApplies) || gdprApplies && !hasConsentBeenCaptured) {
      (0, _applicationState.addStateChangeListener)(_applicationState.State.gdprData, this.initLotame, true);
      return;
    }

    this.initLightningTagScript();
  },


  /**
   * Verify if lotame is enabled by our RadioMill config settings
   * @returns {boolean}
   */
  isLotameEnabled: function isLotameEnabled() {
    var options = (0, _applicationState.getState)(_applicationState.State.options);
    return _parseUtils2.default.parseBool((0, _get2.default)(options, 'lotame.enabled', false));
  },


  /**
   * Callback defined on the global lotame object, created by preConfigureLotame().
   * This callback is invoked internally by Lotame when it's ready. We immediately
   * update user consent, process any commands that were pre-queued up by external processes
   * in Gemini-Web, for instance, before Lotame was ready.
   */
  handleLotameReady: function handleLotameReady() {
    this.setConsent();
  },


  /**
   * Callback defined on the global lotame object, created by preConfigureLotame().
   * This callback is invoked internally by Lotame when it's finished building audiences.
   * We fetch audiences and update our application state accordingly.
   * @param profile
   */
  handleAudiencesReady: function handleAudiencesReady(profile) {
    var audiences = this.fetchAudiences(profile);
    this.updateAudiences(audiences);
  },


  /**
   * The profile object is provided by Lotame and is used to fetch audiences.
   *
   * Executes on:
   *  - init (see handleAudiencesReady() and preConfigureLotame())
   *  - page change (see signalPageChange() and handleRouteChange())
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#audience-callback
   *
   * @param profile
   * @returns {array}
   */
  fetchAudiences: function fetchAudiences(profile) {
    var options = (0, _applicationState.getState)(_applicationState.State.options);
    var audiencesLimit = (0, _get2.default)(options, 'ads.lotame.segmentLimit', DEFAULT_LOTAME_AUDIENCE_LIMIT);
    var audiences = [];

    try {
      audiences = profile.getAudiences(parseInt(audiencesLimit, 10)) || [];
    } catch (error) {
      _logger2.default.error({ message: _constants2.LOTAME_FETCH_AUDIENCE_ERROR, error: error });
    }

    return audiences;
  },


  /**
   * Updates audiences (AKA Lotame Segments) in application state and GoogleTag PubAds Targeting
   * @param audiences
   */
  updateAudiences: function updateAudiences(audiences) {
    (0, _applicationState.updateState)(_applicationState.State.lotamesegments, audiences);

    // https://my.lotame.com/t/m1hh37x/google-ad-manager-guide
    /* eslint-disable no-undef */
    googletag.cmd.push(function () {
      googletag.pubads().setTargeting('lotame', (0, _applicationState.getState)(_applicationState.State.lotamesegments));
    });
    /* eslint-enable no-undef */
  },


  /**
   * Retrieves cached comma-delimited audiences string from local storage and splits
   * it into an array before returning.
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#local-storage-usage
   *
   * @returns {string[]|undefined}
   */
  getCachedAudiences: function getCachedAudiences() {
    // audiences are stored as a comma-delimited string in local storage
    var audiences = _localStorageController2.default.get(_constants.LocalStorage.AUDIENCES);

    if (!audiences || typeof audiences !== 'string') {
      return;
    }

    // eslint-disable-next-line consistent-return
    return audiences.split(',');
  },


  /**
   * Initializes Lotame Lightning Tag script
   */
  initLightningTagScript: function initLightningTagScript() {
    var lotameClientId = this.lotameClientId;
    // https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#lightning-tag-setup
    _htmlUtils2.default.addScript('https://tags.crwdcntrl.net/lt/c/' + lotameClientId + '/lt.min.js', true, 'LOTCC_' + lotameClientId);
  },


  /**
   * Queues addLotameInterests call
   * @param interests
   */
  addInterests: function addInterests(interests) {
    this.queueLotameCommand(this.addLotameInterests, interests);
  },


  /**
   * Invokes Lotame collect method and passes along interests
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#data-object
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#collect
   *
   * @param interests
   */
  addLotameInterests: function addLotameInterests(interests) {
    this.lotame.collect(this.buildDataObject(interests));
  },


  /**
   * Formats Lotame interests
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#data-object
   *
   * @param interests
   * @returns {{behaviors: {int: *}}}
   */
  buildDataObject: function buildDataObject(interests) {
    return {
      behaviors: {
        int: interests
      }
    };
  },


  /**
   * Queues a page change signal command
   * @param interests
   */
  triggerLotameTargeting: function triggerLotameTargeting(interests) {
    this.queueLotameCommand(this.signalPageChange, interests);
  },


  /**
   * Signals to Lotame that a page change has occurred
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#page
   *
   * @param interests
   */
  signalPageChange: function signalPageChange(interests) {
    this.lotame.page(this.buildDataObject(interests));
  },


  /**
   * Queues a set consent command. Only applies when GDPR is not applicable, as
   * Lotame is TCF-compliant and will interface with the TCF API for GDPR consent data.
   * https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#setconsent
   */
  setConsent: function setConsent() {
    var _getState2 = (0, _applicationState.getState)(_applicationState.State.gdprData, {}),
        gdprApplies = _getState2.gdprApplies;

    if (!this.isLotameEnabled() || gdprApplies) {
      return;
    }

    var isUserOptedIn = !(0, _applicationState.getState)(_applicationState.State.isOptedOut, false);

    var consents = {
      analytics: isUserOptedIn,
      crossdevice: isUserOptedIn,
      datasharing: isUserOptedIn,
      targeting: isUserOptedIn
    };

    // using noop, since callback return data isn't actionable for our purposes
    // https://my.lotame.com/t/y4hh3sy/user-consent-guide#callback-data
    this.queueLotameCommand(this.setLotameConsent, _noop2.default, this.lotameClientId, consents);
  },


  /**
   * Invokes the Lotame setConsent method
   * @param args
   */
  setLotameConsent: function setLotameConsent() {
    var _lotame;

    (_lotame = this.lotame).setConsent.apply(_lotame, arguments);
  },


  /**
   * Queues Lotame commands
   * @param command
   * @param args
   */
  queueLotameCommand: function queueLotameCommand(command) {
    try {
      for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      this.lotame.cmd.push(command.bind.apply(command, [this].concat(args)));
    } catch (error) {
      _logger2.default.error({ message: _constants2.LOTAME_COMMAND_QUEUE_ERROR, error: error });
    }
  }
};

exports.default = lotameNetworkController;